<template>
  <div class="assumptions">
    <EnergyDemandSection
      :calories="calories"
      :calories-from-norms="caloriesFromNorms"
      :carbohydrates-grams="carbohydratesGrams"
      :fat-grams="fatGrams"
      :norms-active="normsActive"
      :protein-grams="proteinGrams"
      :sport="energyDemandSport"
      :sport-carbohydrates="sportCarbohydrates"
      :sport-fat="sportFat"
      :sport-protein="sportProtein"
      :standard="energyDemandStandard"
      :standard-carbohydrates="standardCarbohydrates"
      :standard-fat="standardFat"
      :standard-protein="standardProtein"
      :weight="weight"
      @calculation="handleUpdateCalculation"
      @mode="dietStore.updateCalculationType"
      @norm="handleUpdateNorm"
      @weight="updatePatientWeight"
    />
    <MealsSection
      :calories-for-distribution="energyDistributionCalories"
      :delete-open="deleteMealModal"
      :energy-distribution="Boolean(energyDistribution)"
      :hour-options="hourOptions"
      :meals="meals"
      :selected-meal-name="choosenMealName"
      @close-delete="hideDeleteMealModal"
      @confirm-delete="handleDeleteMeal"
      @create="handleCreateMeal"
      @delete="showDeleteMealModal"
      @toggle-distribution="handleUpdateEnergyDistribution"
    />
    <NormsSection
      :active="normsActive"
      :calculated-norm="calculatedNorm"
      :can-edit="hasPerm('accounts.diet_norms_edit_values')"
      :get-precision="getPrecisionForNutrients"
      :get-step="getStepForNutrients"
      :inputed-norm="inputedNorm"
      :patient="patientBindings"
      :patient-listeners="patientListeners"
      @toggle="handleUpdateNormActive"
      @update="handleUpdateNorm"
      @upgrade="showAppUpgradePlanModal"
    />
  </div>
</template>

<script lang="ts" setup>
  import { useDietsStore } from '@/store/dietsStore'

  import EnergyDemandSection from './components/EnergyDemandSection/EnergyDemandSection.vue'
  import MealsSection from './components/MealsSection/MealsSection.vue'
  import NormsSection from './components/NormsSection/NormsSection.vue'
  import { useEnergyDemand } from './composables/useEnergyDemand'
  import { useMeals } from './composables/useMeals'
  import { usePatientNorms } from './composables/usePatientNorms'

  const dietStore = useDietsStore()
  const {
    calories,
    caloriesFromNorms,
    carbohydratesGrams,
    energyDemandSport,
    energyDemandStandard,
    fatGrams,
    handleUpdateCalculation,
    normsActive,
    proteinGrams,
    sportCarbohydrates,
    sportFat,
    sportProtein,
    standardCarbohydrates,
    standardFat,
    standardProtein,
    weight,
  } = useEnergyDemand()
  const {
    deleteMealModal,
    energyDistribution,
    energyDistributionCalories,
    handleCreateMeal,
    handleDeleteMeal,
    handleUpdateEnergyDistribution,
    hideDeleteMealModal,
    hourOptions,
    meals,
    selectedMealName: choosenMealName,
    showDeleteMealModal,
  } = useMeals()
  const {
    calculatedNorm,
    getPrecisionForNutrients,
    getStepForNutrients,
    handleUpdateNorm,
    handleUpdateNormActive,
    hasPerm,
    inputedNorm,
    patientBindings,
    patientListeners,
    showAppUpgradePlanModal,
    updatePatientWeight,
  } = usePatientNorms()
</script>

<style lang="scss">
.assumptions {
  .section {
    margin-bottom: 24px;
    background: #fff;
    box-shadow: 2px 4px 4px 0 rgb(232 232 232 / 50%);
  }

  .section__header {
    padding: 16px;
    font-family: Montserrat-Medium;
    font-size: 24px;
  }

  .info {
    padding: 16px;
    font-size: 11px;
    line-height: 20px;
    color: var(--color-secondary-text);
  }

  .info .base-icon {
    color: var(--color-warning-100);
  }

  .info--bold {
    font-family: Montserrat-Medium;
    color: var(--color-primary-text);
  }

  .label {
    font-family: Montserrat-Medium;
    font-size: 12px;
  }

  .energy-demand .info {
    padding: 0 16px 16px;
  }

  .meals {
    position: relative;
  }

  .meals__content {
    padding: 0 16px 16px;
  }

  .energy-distribution-switch {
    position: absolute;
    top: 16px;
    right: 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: Montserrat-Medium;
    font-size: 12px;
    cursor: pointer;
  }

  .energy-distribution-switch__label {
    margin-right: 12px;
    cursor: pointer;
  }

  .meal-header {
    display: grid;
    grid-template-columns: 80px auto 140px 30px;
    column-gap: 30px;
    padding: 12px 0;
    margin-top: 4px;
    font-family: Montserrat-Medium;
    font-size: 12px;
    border-bottom: 1px solid #eaeaea;
  }

  .meal-header.energy-distribution {
    grid-template-columns: 80px auto 140px 120px 30px;
  }

  .meal-header div {
    padding-left: 6px;
  }

  div.meal-header__hour {
    padding-left: 12px;
  }

  .meal {
    display: grid;
    grid-template-columns: 80px auto 140px 30px;
    column-gap: 30px;
    align-items: center;
    height: 40px;
  }

  .meal.energy-distribution {
    grid-template-columns: 80px auto 140px 120px 30px;
  }

  .meal-hour {
    width: 80px;
  }

  .meal-hour__select {
    width: 68px;
    margin: auto;
  }

  .meal-hour__select .base-select__selected {
    padding: 5px 21px 5px 8px;
    text-align: center;
  }

  .meal-name__input {
    width: 100%;
  }

  .meal-type {
    width: 140px;
  }

  .meal-type__select .base-select__selected {
    padding: 5px 21px 5px 8px;
  }

  .meal-energy-distrubution {
    width: 120px;
    font-size: 12px;
  }

  .meal-energy-distrubution__input {
    width: 36px;
    text-align: center;
  }

  .meal-energy-distrubution__calories {
    margin-left: 6px;
  }

  .meal-action {
    width: 30px;
  }

  .create-meal {
    margin-top: 12px;
  }

  .energy-demand-type {
    display: flex;
    flex-direction: row;
    margin: 0 16px;
  }

  .energy-demand-type__radio {
    margin-left: 20px;
  }

  .energy-demand__sport,
  .energy-demand__standard {
    display: grid;
    grid-template-columns: 265px 1fr 1fr 1fr;
    gap: 6px;
    padding: 24px 6px 16px;
  }

  .energy-demand__sport {
    margin-bottom: 0;
  }

  .energy-demand__header {
    height: 32px;
    font-family: Montserrat-Medium;
    font-size: 12px;
    line-height: 32px;
    text-align: center;
  }

  .energy-demand__calories .energy-demand__header,
  .energy-demand__weight .energy-demand__header {
    background: #f5f5f5;
  }

  .energy-demand__protein .energy-demand__header {
    color: #fff;
    background: #f16595;
  }

  .energy-demand__fat .energy-demand__header {
    color: #fff;
    background: #f1c365;
  }

  .energy-demand__carbohydrates .energy-demand__header {
    color: #fff;
    background: #9f98ff;
  }

  .energy-demand__info {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    margin: 8px;
    font-family: Montserrat-Medium;
    font-size: 9px;
    color: var(--color-tertiary-text);
  }

  .energy-demand__info-tooltip-content {
    display: block;
    width: 200px;
    text-align: center;
  }

  .energy-demand__info-tooltip {
    width: max-content;
  }

  .energy-demand__calories-box,
  .energy-demand__weight-box {
    display: flex;
    justify-content: center;
  }

  .energy-demand__box {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    font-size: 12px;
  }

  .energy-demand__box-left {
    width: calc(100% - 120px);
  }

  .energy-demand__input {
    width: 60px;
    text-align: center;
  }

  .norms__header {
    height: 32px;
    padding: 0 12px;
    font-family: Montserrat-Medium;
    font-size: 12px;
    line-height: 32px;
    color: #fff;
    text-transform: uppercase;
    background: var(--color-main-100);
  }

  .norms__box {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: max-content;
    margin: 0 16px;
    cursor: pointer;
  }

  .norms__switch {
    margin-left: 12px;
  }

  .norms__elements {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 8px;
    margin: 8px 6px;
  }

  .norms__element {
    min-width: 250px;
  }

  .norms__element-name {
    height: 32px;
    font-size: 12px;
    line-height: 32px;
    text-align: center;
    background: #f5f5f5;
  }

  .norms__element-name-tooltip {
    display: block;
    width: max-content;
    margin: auto;
  }

  .norms__element-name-tooltip-content {
    display: block;
    max-width: 200px;
  }

  .norms__element-data {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    padding: 16px 8px 8px;
  }

  .norms__element-calculated {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .norms__element-input {
    width: 60px;
    text-align: center;
  }

  .norms__element-input:disabled {
    color: inherit;
    border-bottom: none;
  }

  .norms__element-dummy {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 25px;
    padding: 0 8px;
    font-size: 12px;
    color: rgb(166 166 166);
    text-align: center;
    border-bottom: 1px solid var(--color-tertiary-text);
  }

  .norms__element-dummy:hover {
    border-color: var(--color-main-100);
  }

  .norms__element-measurement {
    position: relative;
    top: -6px;
    height: 0;
    font-family: Montserrat-Medium;
    font-size: 9px;
    color: var(--color-tertiary-text);
  }

  .norms__element-calculated-value {
    height: 25px;
    font-size: 12px;
    line-height: 25px;
    color: var(--color-primary-text);
  }

  .norms__patient {
    grid-template-columns: repeat(3, 1fr);
  }

  /* .norms__patient-age {
              grid-column-end: span 1;
            } */
  .norms__patient-age .norms__element-data {
    justify-content: center;
  }

  /*  .norms__patient-gender {
              grid-column-end: span 1;
            } */
  .norms__patient-gender .norms__element-data {
    align-items: center;
    justify-content: space-evenly;
    height: 26px;
  }

  .norms__patient-pregnancy-lactation {
    grid-row-end: span 3;
    grid-column-end: span 1;
  }

  .norms__patient-pregnancy-lactation .norms__element-data {
    align-items: flex-start;
    justify-content: space-evenly;
  }

  .norms__patient-pregnancy,
  .norms__patient-lactation {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .norms__patient-pregnancy-active,
  .norms__patient-lactation-active {
    margin-bottom: 8px;
  }

  .norms__patient-pregnancy-trimester,
  .norms__patient-lactation-period {
    margin-top: 16px;
  }

  .norms__patient-weight .norms__element-data,
  .norms__patient-height .norms__element-data,
  .norms__patient-activity-level .norms__element-data {
    justify-content: center;
  }

  .norms__patient-weight .base-input,
  .norms__patient-height .base-input {
    width: 60px;
    text-align: center;
  }

  .norms__patient-weight span,
  .norms__patient-height span {
    display: inline-block;
    margin-left: 6px;
    font-size: 12px;
  }

  .norms__patient-activity-level {
    grid-column-end: span 2;
  }

  @media only screen and (width >= 1400px) {
    .norms__patient {
      grid-template-columns: repeat(6, 1fr);
    }

    .norms__patient-age {
      grid-column-end: span 2;
    }

    .norms__patient-gender {
      grid-column-end: span 2;
    }

    .norms__patient-pregnancy-lactation {
      grid-row-end: span 2;
      grid-column-end: span 2;
    }

    .norms__patient-activity-level {
      grid-column-end: span 2;
    }
  }
}
</style>
